import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Check, Plus, Trash2, ChevronDown } from "lucide-react";

/**
 * Reusable multi-select dropdown for Categories and Labels.
 * Props:
 *   items: [{ id, name, color }]
 *   selectedIds: string[]
 *   onToggle: (id, isSelected) => void
 *   onCreate: (name) => Promise<void>
 *   onDelete: (id) => void
 *   maxItems: number
 *   label: string (e.g. "Category" or "Labels")
 *   icon: React component
 *   counterLabel: string (e.g. "categories" or "labels")
 */
export default function AssignmentDropdown({
  items, selectedIds, onToggle, onCreate, onDelete,
  maxItems = 10, label, icon: Icon, counterLabel, showColorDot = true,
}) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const newInputId = useId();

  const canCreate = typeof onCreate === "function";
  const canDelete = typeof onDelete === "function";
  const canToggle = typeof onToggle === "function";

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (!canCreate) return;
    setCreating(true);
    try {
      await onCreate(trimmed);
      setNewName("");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (id) => {
    if (!canDelete) return;
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const selectedCount = selectedIds?.length || 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`text-[#555555] hover:text-[#111111] gap-1.5 ${selectedCount > 0 ? "font-medium" : ""}`}
          data-testid={`toolbar-${counterLabel}-trigger`}
        >
          {Icon && <Icon size={15} />}
          {label}
          {selectedCount > 0 && (
            <span className="ml-0.5 text-[10px] bg-zinc-200 text-[#888888] px-1.5 py-0.5 rounded-full font-mono">
              {selectedCount}
            </span>
          )}
          <ChevronDown size={12} className="text-zinc-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" data-testid={`toolbar-${counterLabel}-dropdown`}>
        {/* Item list */}
        <div className="max-h-[280px] overflow-y-auto py-1">
          {items.length === 0 && (
            <p className="text-xs text-zinc-400 text-center py-3" data-testid={`${counterLabel}-empty`}>
              {canCreate
                ? `No ${counterLabel} yet`
                : `${counterLabel.charAt(0).toUpperCase() + counterLabel.slice(1)} are coming soon`}
            </p>
          )}
          {items.map((item) => {
            const isSelected = selectedIds?.includes(item.id);
            const isDeleting = deleteConfirm === item.id;

            return (
              <div
                key={item.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F5F5F5] group"
                data-testid={`${counterLabel}-item-${item.id}`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => canToggle && onToggle(item.id, isSelected)}
                  disabled={!canToggle}
                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isSelected
                      ? "bg-zinc-900 border-zinc-900"
                      : "border-zinc-300 hover:border-zinc-400"
                  } ${!canToggle ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid={`${counterLabel}-toggle-${item.id}`}
                >
                  {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                </button>

                {/* Color dot + name */}
                {showColorDot && (
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                )}
                <span className="text-sm text-[#555555] flex-1 truncate">{item.name}</span>

                {/* Delete — only if caller supplied an onDelete handler */}
                {canDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    className={`p-0.5 rounded transition-colors shrink-0 ${
                      isDeleting
                        ? "text-red-700 bg-red-50"
                        : "text-red-500 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 focus:opacity-100"
                    }`}
                    title={isDeleting ? "Click again to confirm" : "Delete"}
                    data-testid={`${counterLabel}-delete-${item.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Create new — only if caller supplied an onCreate handler.
            The whole row is a <label htmlFor=…> so clicks anywhere on the
            row (Plus icon, padding, gap) focus the underlying input. Without
            this wrapper, only clicks exactly on the input text area would
            register and the row appeared "dead". */}
        {canCreate && items.length < maxItems && (
          <label
            htmlFor={newInputId}
            className="border-t border-[#E5E5E5] px-3 py-2 flex items-center gap-1.5 cursor-text hover:bg-[#FAFAFA] transition-colors block"
            data-testid={`${counterLabel}-new-row`}
          >
            <Plus size={13} className="text-zinc-400 shrink-0" />
            <Input
              id={newInputId}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder={`New ${counterLabel.slice(0, -1)}...`}
              className="h-7 text-xs border-0 shadow-none px-1 focus-visible:ring-0 bg-transparent"
              data-testid={`${counterLabel}-new-input`}
            />
            {newName.trim() && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCreate}
                disabled={creating}
                className="h-6 px-2 text-xs text-blue-600"
                data-testid={`${counterLabel}-create-btn`}
              >
                Add
              </Button>
            )}
          </label>
        )}

        {/* Counter */}
        <div className="border-t border-[#E5E5E5] px-3 py-2">
          <p className="text-[10px] text-zinc-400" data-testid={`${counterLabel}-counter`}>
            {items.length} / {maxItems} {counterLabel} used
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
